export const DEFAULT_MEMBERS_PORTAL_ACCENT = '#E9B76D'
export const DEFAULT_MEMBERS_PORTAL_THEME_MODE = 'dark'

const THEME_PALETTES = {
  dark: {
    '--mp-bg': '#0B1322',
    '--mp-surface': '#0F1520',
    '--mp-card': '#101C2D',
    '--mp-text': '#F6F7FB',
    '--mp-muted': '#C7D1DF',
    '--mp-text-muted': '#C7D1DF',
    '--mp-border': 'rgba(255, 255, 255, 0.08)',
    '--mp-elevation': '0 18px 40px rgba(0, 0, 0, 0.25)',
    '--mp-bg-grad': 'linear-gradient(180deg, #0c1727 0%, #0a0f1a 60%, #0a0c12 100%)',
    '--mp-topnav-bg': 'rgba(8, 13, 23, 0.9)',
    '--mp-surface-2': '#111B2C',
    '--mp-panel-bg': 'linear-gradient(165deg, rgba(16, 27, 45, 0.96), rgba(11, 19, 35, 0.98))',
    '--mp-panel-border': 'rgba(255, 255, 255, 0.10)',
    '--mp-panel-shadow': '0 20px 45px rgba(0, 0, 0, 0.30)',
    '--mp-glass-bg': 'rgba(255, 255, 255, 0.06)',
    '--mp-glass-border': 'rgba(255, 255, 255, 0.14)',
    '--mp-open-group-avatar-bg': 'rgba(255, 255, 255, 0.06)',
    '--mp-open-group-modal-backdrop': 'rgba(5, 10, 22, 0.72)',
    '--mp-open-group-modal-panel-bg': 'linear-gradient(165deg, rgba(13, 20, 35, 0.96), rgba(9, 15, 28, 0.94))',
    '--mp-open-group-modal-panel-shadow': '0 22px 60px rgba(0, 0, 0, 0.52), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
    '--mp-open-group-modal-meta-bg': 'rgba(255, 255, 255, 0.03)',
    '--mp-open-group-modal-section-bg': 'rgba(255, 255, 255, 0.025)',
    '--mp-open-group-modal-chip-border': 'rgba(255, 255, 255, 0.16)',
    '--mp-open-group-modal-chip-bg': 'rgba(255, 255, 255, 0.04)',
  },
  light: {
    '--mp-bg': '#F6F1E7',
    '--mp-surface': '#FBF7EF',
    '--mp-card': '#FFFDF8',
    '--mp-text': '#121826',
    '--mp-muted': '#4B5565',
    '--mp-text-muted': '#4B5565',
    '--mp-border': 'rgba(18, 24, 38, 0.10)',
    '--mp-elevation': '0 18px 40px rgba(18, 24, 38, 0.10)',
    '--mp-bg-grad': 'linear-gradient(180deg, #FBF7EF 0%, #F6F1E7 55%, #EFE7DA 100%)',
    '--mp-topnav-bg': 'rgba(251, 247, 239, 0.78)',
    '--mp-surface-2': '#F3ECE0',
    '--mp-panel-bg': 'linear-gradient(165deg, rgba(255, 252, 246, 0.96), rgba(246, 239, 228, 0.98))',
    '--mp-panel-border': 'rgba(18, 24, 38, 0.12)',
    '--mp-panel-shadow': '0 18px 36px rgba(18, 24, 38, 0.10)',
    '--mp-glass-bg': 'rgba(255, 255, 255, 0.72)',
    '--mp-glass-border': 'rgba(255, 255, 255, 0.95)',
    '--mp-open-group-avatar-bg': 'rgba(255, 255, 255, 0.84)',
    '--mp-open-group-modal-backdrop': 'rgba(18, 24, 38, 0.28)',
    '--mp-open-group-modal-panel-bg': 'linear-gradient(165deg, rgba(255, 252, 246, 0.98), rgba(246, 239, 228, 0.96))',
    '--mp-open-group-modal-panel-shadow': '0 18px 36px rgba(18, 24, 38, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.65)',
    '--mp-open-group-modal-meta-bg': 'rgba(255, 255, 255, 0.86)',
    '--mp-open-group-modal-section-bg': 'rgba(255, 255, 255, 0.82)',
    '--mp-open-group-modal-chip-border': 'rgba(18, 24, 38, 0.14)',
    '--mp-open-group-modal-chip-bg': 'rgba(255, 255, 255, 0.92)',
  },
}

export function resolveHeroOverlayOpacity(value) {
  const numeric = Number(value)
  const clamped = Number.isFinite(numeric) ? Math.min(0.85, Math.max(0, numeric)) : 0.45
  return Number(clamped.toFixed(2))
}

export function hexToRgb(hex) {
  if (typeof hex !== 'string' || !/^#([0-9A-Fa-f]{6})$/.test(hex.trim())) {
    throw new Error('invalid_hex_color')
  }

  const normalized = hex.trim()
  const r = Number.parseInt(normalized.slice(1, 3), 16)
  const g = Number.parseInt(normalized.slice(3, 5), 16)
  const b = Number.parseInt(normalized.slice(5, 7), 16)
  return `${r}, ${g}, ${b}`
}

export function applyPortalTheme({
  accentColor = DEFAULT_MEMBERS_PORTAL_ACCENT,
  themeMode = DEFAULT_MEMBERS_PORTAL_THEME_MODE,
} = {}) {
  const root = document.documentElement
  const resolvedMode = themeMode === 'light' ? 'light' : DEFAULT_MEMBERS_PORTAL_THEME_MODE
  const resolved = /^#([0-9A-Fa-f]{6})$/.test(accentColor) ? accentColor.toUpperCase() : DEFAULT_MEMBERS_PORTAL_ACCENT
  const rgb = hexToRgb(resolved)

  Object.entries(THEME_PALETTES[resolvedMode]).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })
  root.dataset.theme = resolvedMode

  root.style.setProperty('--mp-accent', resolved)
  root.style.setProperty('--accent-color', resolved)
  root.style.setProperty('--mp-accent-rgb', rgb)
  root.style.setProperty('--mp-accent-contrast', '#0b0f1a')
  root.style.setProperty('--mp-accent-soft', `rgba(${rgb}, 0.1)`)
  root.style.setProperty('--mp-accent-20', `rgba(${rgb}, 0.2)`)
  root.style.setProperty('--mp-accent-35', `rgba(${rgb}, 0.35)`)
  root.style.setProperty('--mp-accent-55', `rgba(${rgb}, 0.55)`)
  root.style.setProperty('--mp-accent-75', `rgba(${rgb}, 0.75)`)
  root.style.setProperty('--mp-accent-grad', `linear-gradient(120deg, rgba(${rgb}, 0.95), rgba(${rgb}, 0.75))`)
  root.style.setProperty('--mp-open-group-modal-hero-border', `rgba(${rgb}, 0.35)`)
  root.style.setProperty('--mp-open-group-modal-hero-bg', `linear-gradient(140deg, rgba(${rgb}, 0.2), var(--mp-glass-bg))`)
}
